import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Grid,
    Modal,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import Slider from 'react-slick';
import { useState } from 'react';
import formatCurrency from 'utils/formatCurrency';

const RoomTypeList = ({
    roomTypes,
    loadingRooms,
    onSelect,
    selectedId,
    selectable = false,
    disableSelect = false,
}) => {
    const theme = useTheme();
    const [openCarousel, setOpenCarousel] = useState(false);
    const [selectedImages, setSelectedImages] = useState([]);
    const getAssetUrl = (img) =>
        `/assets/hotel-rooms/${String(img || '')
            .split('/')
            .pop()}`;

    const handleOpenCarousel = (images) => {
        setSelectedImages(images || []);
        setOpenCarousel(true);
    };

    return (
        <>
            <Modal open={openCarousel} onClose={() => setOpenCarousel(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', md: '80%' },
                        maxHeight: '80vh',
                        bgcolor: 'background.paper',
                        p: 2,
                        borderRadius: 2,
                    }}
                >
                    {selectedImages.length > 0 && (
                        <Box
                            sx={{
                                '& .slick-prev, & .slick-next': {
                                    zIndex: 3,
                                    width: 64,
                                    height: 64,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                },
                                '& .slick-prev': { left: 16 },
                                '& .slick-next': { right: 16 },
                                '& .slick-prev:before, & .slick-next:before': {
                                    fontSize: 48,
                                    color: theme.palette.secondary.main,
                                },
                                '& .slick-slide img': { objectFit: 'contain' },
                            }}
                        >
                            <Slider
                                key={selectedImages.join('|')}
                                dots
                                infinite
                                speed={500}
                                slidesToShow={1}
                                slidesToScroll={1}
                                arrows
                                adaptiveHeight
                            >
                                {selectedImages.map((img, index) => (
                                    <div key={index}>
                                        <img
                                            src={getAssetUrl(img)}
                                            alt={`Image ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                maxHeight: '70vh',
                                            }}
                                        />
                                    </div>
                                ))}
                            </Slider>
                        </Box>
                    )}
                </Box>
            </Modal>

            <Grid container spacing={3}>
                {loadingRooms &&
                    Array.from({ length: 2 }).map((_, idx) => (
                        <Grid item xs={12} md={6} key={`skeleton-${idx}`}>
                            <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <Skeleton variant="rectangular" height={280} />
                                <CardContent>
                                    <Skeleton width="60%" />
                                    <Skeleton width="80%" />
                                    <Skeleton width="40%" />
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Skeleton
                                        variant="rectangular"
                                        width={120}
                                        height={36}
                                        sx={{ mr: 2 }}
                                    />
                                    <Skeleton
                                        variant="rectangular"
                                        width={120}
                                        height={36}
                                    />
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}

                {!loadingRooms &&
                    roomTypes.map((room) => (
                        <Grid item xs={12} md={6} key={room.id}>
                            <Card
                                sx={{
                                    borderRadius: 3,
                                    boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        overflow: 'hidden',
                                        height: { xs: 180, md: 280 },
                                    }}
                                >
                                    {room.data?.images?.length > 0 ? (
                                        room.data.images.length === 1 ? (
                                            <img
                                                src={getAssetUrl(
                                                    room.data.images[0],
                                                )}
                                                alt={room.name}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() =>
                                                    handleOpenCarousel(
                                                        room.data.images,
                                                    )
                                                }
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    height: '100%',
                                                    '& .slick-list, & .slick-slider, & .slick-track, & .slick-slide, & .slick-slide > div':
                                                        { height: '100%' },
                                                    '& .slick-slide img': {
                                                        objectFit: 'cover',
                                                        height: '100%',
                                                        width: '100%',
                                                    },
                                                    '& .slick-prev, & .slick-next':
                                                        {
                                                            zIndex: 2,
                                                            width: 44,
                                                            height: 44,
                                                            top: '50%',
                                                            transform:
                                                                'translateY(-50%)',
                                                        },
                                                    '& .slick-prev': {
                                                        left: 8,
                                                    },
                                                    '& .slick-next': {
                                                        right: 8,
                                                    },
                                                    '& .slick-prev:before, & .slick-next:before':
                                                        {
                                                            fontSize: 36,
                                                            color: theme.palette
                                                                .primary.main,
                                                        },
                                                }}
                                            >
                                                <Slider
                                                    dots={false}
                                                    infinite
                                                    speed={500}
                                                    slidesToShow={1}
                                                    slidesToScroll={1}
                                                    arrows
                                                    autoplay
                                                    autoplaySpeed={4000}
                                                >
                                                    {room.data.images.map(
                                                        (img, index) => (
                                                            <div key={index}>
                                                                <img
                                                                    src={getAssetUrl(
                                                                        img,
                                                                    )}
                                                                    alt={`${
                                                                        room.name
                                                                    } ${
                                                                        index +
                                                                        1
                                                                    }`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onClick={() =>
                                                                        handleOpenCarousel(
                                                                            room
                                                                                .data
                                                                                .images,
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        ),
                                                    )}
                                                </Slider>
                                            </Box>
                                        )
                                    ) : null}

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            background:
                                                'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.45) 100%)',
                                        }}
                                    />
                                {!room.isAvailabel && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 12,
                                            left: 12,
                                            bgcolor: 'error.main',
                                            color: 'error.contrastText',
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: 1.5,
                                            fontWeight: 700,
                                            fontSize: 12,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        }}
                                    >
                                        Sold Out
                                    </Box>
                                )}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 12,
                                            left: 12,
                                            bgcolor: 'rgba(255,255,255,0.92)',
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={700}
                                            color="warning.main"
                                        >
                                            {formatCurrency(
                                                parseInt(room.price),
                                            )}{' '}
                                            / night
                                        </Typography>
                                    </Box>
                                </Box>

                                <CardContent>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                    >
                                        {room.name}
                                    </Typography>
                                    {Array.isArray(room.data?.features) &&
                                        room.data.features.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    sx={{
                                                        flexWrap: 'wrap',
                                                        gap: 0.75,
                                                        maxHeight: 120,
                                                        overflow: 'auto',
                                                        pr: 0.5,
                                                    }}
                                                >
                                                    {room.data.features.map(
                                                        (feature, idx) => (
                                                            <Tooltip
                                                                key={idx}
                                                                title={feature}
                                                            >
                                                                <Chip
                                                                    label={
                                                                        feature
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        ),
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}
                                </CardContent>

                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() =>
                                            handleOpenCarousel(
                                                room.data?.images || [],
                                            )
                                        }
                                        sx={{ ml: 1 }}
                                    >
                                        View Photos
                                    </Button>
                                    {selectable && (
                                        <Button
                                            variant={'contained'}
                                            color={
                                                selectedId === room.id
                                                    ? 'primary'
                                                    : 'secondary'
                                            }
                                            onClick={() =>
                                                onSelect && onSelect(room)
                                            }
                                            disabled={disableSelect || !room.isAvailabel}
                                            sx={{ ml: 1 }}
                                        >
                                            {selectedId === room.id
                                                ? 'Selected'
                                                : !room.isAvailabel
                                                ? 'Sold Out'
                                                : 'Book Room'}
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
            </Grid>
        </>
    );
};

export default RoomTypeList;
